/* eslint-disable */
import { useEffect, useRef } from "react";

const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js";

// Cloudflare's documented test sitekey — always passes, works on any
// hostname. Pairs with the matching dummy secret on the backend,
// which is short-circuited in DEBUG mode by services/captcha.py.
const DUMMY_PASSING_SITEKEY = "1x00000000000000000000AA";

// Production sitekey — bound to the registered hostnames in Cloudflare.
// Pulled from env at build time so the same image can target different
// environments.
const PROD_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

function isDevHost() {
  if (typeof window === "undefined" || !window.location) return false;
  const h = window.location.hostname || "";
  return (
    h === "localhost" ||
    h === "127.0.0.1" ||
    h.endsWith(".trycloudflare.com") ||
    h.endsWith(".ngrok-free.app") ||
    h.endsWith(".lvh.me")
  );
}

// On dev/preview hosts use the dummy sitekey so the widget actually
// renders. Production hostnames get the real key bound in Cloudflare.
const SITE_KEY = isDevHost()
  ? DUMMY_PASSING_SITEKEY
  : PROD_SITE_KEY || DUMMY_PASSING_SITEKEY;

let scriptPromise = null;
function loadScript() {
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    if (window.turnstile) {
      resolve();
      return;
    }
    const existing = document.querySelector(`script[src="${SCRIPT_SRC}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", reject);
      return;
    }
    const s = document.createElement("script");
    s.src = SCRIPT_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = reject;
    document.head.appendChild(s);
  });
  return scriptPromise;
}

/**
 * Cloudflare Turnstile widget. Renders the challenge inline and
 * fires `onToken(token)` once the user solves it. Re-renders on the
 * same DOM node if `resetKey` changes (so the parent can force a
 * fresh challenge after a failed submit).
 */
export default function TurnstileWidget({
  onToken,
  onError,
  onExpire,
  theme = "auto",
  resetKey = 0,
}) {
  const ref = useRef(null);
  const widgetIdRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    loadScript()
      .then(() => {
        if (cancelled || !ref.current || !window.turnstile) return;
        // Tear down any previous instance so we don't end up with two
        // challenges painted into the same div.
        if (widgetIdRef.current) {
          try {
            window.turnstile.remove(widgetIdRef.current);
          } catch (_) {}
          widgetIdRef.current = null;
        }
        widgetIdRef.current = window.turnstile.render(ref.current, {
          sitekey: SITE_KEY,
          theme,
          callback: (token) => onToken && onToken(token),
          "error-callback": () => onError && onError(),
          "expired-callback": () => onExpire && onExpire(),
        });
      })
      .catch(() => onError && onError());

    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch (_) {}
        widgetIdRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);

  return <div ref={ref} className="vsc-turnstile" />;
}
